<template>
	<div class="date-picker">
		<vue-date-picker
			ref="datepicker"
			class="date-picker__control"
			v-on="$listeners"
			:value="normalizedValue"
			:default-value="normalizedInitialDate"
			:lang="datepickerLanguage"
			:placeholder="$attrs.placeholder || ''"
			:disabled="isDisabled"
			:disabled-date="isDateDisabled"
			:input-attr="inputAttributes"
			:class="{'date-picker__control--invalid': validationState === false}"
			format="DD/MM/YYYY"
			value-type="YYYY-MM-DD"
			input-class="datetime-input"
			:clearable="false"
			:append-to-body="true"
			@open="handleOpen"
		>
			<template slot="icon-calendar">
				<img class="calendar-icon" src="/static/buttons/new/calendar.svg" alt="">
			</template>
		</vue-date-picker>
		<input
			v-if="required"
			class="date-picker__native-validation"
			type="text"
			:value="normalizedValue"
			required
			tabindex="-1"
			aria-hidden="true"
			@invalid="handleInvalid"
		/>
	</div>
</template>

<script>
import moment from 'moment';
import VueDatePicker from 'vue2-datepicker';
import en from 'vue2-datepicker/locale/es/en';
import es from 'vue2-datepicker/locale/es/es';
import ptBR from 'vue2-datepicker/locale/es/pt-br';
import 'vue2-datepicker/index.css';
import { translate } from '../../assets/translations';

export default {
	inheritAttrs: false,
	components: {
		VueDatePicker,
	},
	data() {
		return {
			nativeInvalid: false,
		};
	},
	props: {
		value: {
			type: String,
			default: null,
		},
		invalid: {
			type: Boolean,
			default: false,
		},
		initialDefaultDate: {
			type: String,
			default: null,
		},
		minDate: {
			type: String,
			default: null,
		},
		maxDate: {
			type: String,
			default: null,
		},
		disabledDates: {
			type: Array,
			default() {
				return [];
			},
		},
		mondayFirst: {
			type: Boolean,
			default: false,
		},
		required: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		language() {
			return this.$store.state.language;
		},
		datepickerLanguage() {
			const selectedLanguage = {
				pt: ptBR,
				en,
				es,
			}[this.language.lang] || ptBR;

			return Object.assign({}, selectedLanguage, {
				formatLocale: Object.assign({}, selectedLanguage.formatLocale, {
					firstDayOfWeek: this.mondayFirst ? 1 : 0,
				}),
				monthBeforeYear: true,
			});
		},
		inputAttributes() {
			const attributes = Object.assign({}, this.$attrs);
			delete attributes.disabled;
			delete attributes.max;
			delete attributes.min;
			delete attributes.placeholder;

			return Object.assign(attributes, {
				'aria-invalid': this.validationState === false ? 'true' : null,
				'aria-label': attributes['aria-label'] || this.translation.calendar,
				'aria-required': this.required ? 'true' : null,
			});
		},
		isDisabled() {
			return this.$attrs.disabled !== undefined && this.$attrs.disabled !== false;
		},
		normalizedInitialDate() {
			const normalizedDate = this.normalizeDate(this.initialDefaultDate);
			return normalizedDate ? moment(normalizedDate, 'YYYY-MM-DD', true).toDate() : null;
		},
		normalizedMaxDate() {
			return this.normalizeDate(this.maxDate || this.$attrs.max);
		},
		normalizedMinDate() {
			return this.normalizeDate(this.minDate || this.$attrs.min);
		},
		normalizedValue() {
			return this.normalizeDate(this.value) || '';
		},
		translation() {
			return translate(this.language).datepicker;
		},
		validationState() {
			return this.invalid || this.nativeInvalid ? false : null;
		},
	},
	watch: {
		value() {
			this.nativeInvalid = false;
		},
	},
	methods: {
		handleOpen() {
			if (this.normalizedValue || !this.normalizedInitialDate) return;

			this.$nextTick(() => {
				if (this.$refs.datepicker) {
					this.$refs.datepicker.currentValue = new Date(this.normalizedInitialDate.getTime());
				}
			});
		},
		handleInvalid(event) {
			event.preventDefault();
			this.nativeInvalid = true;
		},
		isDateDisabled(date) {
			const selectedDate = moment(date).startOf('day');
			if (!selectedDate.isValid()) return false;

			const minDate = moment(this.normalizedMinDate, 'YYYY-MM-DD', true);
			if (minDate.isValid() && selectedDate.isBefore(minDate, 'day')) return true;

			const maxDate = moment(this.normalizedMaxDate, 'YYYY-MM-DD', true);
			if (maxDate.isValid() && selectedDate.isAfter(maxDate, 'day')) return true;

			return this.disabledDates.some(item => {
				if (Array.isArray(item)) {
					const start = moment(this.normalizeDate(item[0]), 'YYYY-MM-DD', true);
					const end = moment(this.normalizeDate(item[1]), 'YYYY-MM-DD', true);
					return start.isValid() && end.isValid() && selectedDate.isBetween(start, end, 'day', '[)');
				}

				const disabledDate = moment(this.normalizeDate(item), 'YYYY-MM-DD', true);
				return disabledDate.isValid() && selectedDate.isSame(disabledDate, 'day');
			});
		},
		normalizeDate(value) {
			if (!value) return null;

			const date = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
			return date.isValid() ? date.format('YYYY-MM-DD') : null;
		},
	},
};
</script>

<style lang="scss" scoped>
.date-picker {
	position: relative;
	width: 100%;
}

.date-picker__control,
.date-picker /deep/ .mx-datepicker,
.date-picker /deep/ .mx-input-wrapper,
.date-picker /deep/ .datetime-input {
	width: 100%;
}

.date-picker /deep/ .mx-datepicker,
.date-picker /deep/ .mx-input-wrapper,
.date-picker /deep/ .datetime-input {
	height: 100%;
}

.date-picker /deep/ .datetime-input {
	box-sizing: border-box;
	font-family: inherit;
}

.date-picker /deep/ .mx-icon-calendar {
	position: static;
	width: 0;
	height: 0;
}

.date-picker /deep/ .calendar-icon {
	position: absolute;
	right: 10px;
	bottom: calc(50% - 12px);
	z-index: 1;
	width: 24px;
	pointer-events: none;
}

.date-picker /deep/ .datetime-input:focus {
	border-color: #66acc6;
	box-shadow: 0 0 0 .2rem rgba(102, 172, 198, .15);
}

.date-picker__control--invalid /deep/ .datetime-input {
	border-color: #dc3545 !important;
}

.date-picker__native-validation {
	position: absolute;
	left: 50%;
	bottom: 0;
	width: 1px;
	height: 1px;
	padding: 0;
	border: 0;
	opacity: 0;
	pointer-events: none;
}
</style>

<style lang="scss">
.mx-btn:hover {
	background-color: #f3f3f3;
}
</style>



// WEBPACK FOOTER //
// src/components/shared/DatePicker.vue